// Which account you are looking at, and how to reach the others.
//
// THREE THINGS IT DELIBERATELY DOES, each because the naive version was wrong:
//
// 1. IT FETCHES NOTHING UNTIL SOMEBODY OPENS IT. The account list used to ride along on
//    /api/team/me, so a person holding twenty sub-accounts paid for twenty rows on every
//    page load, to render a control most of them never touch. Identity now carries a
//    COUNT — enough to know whether there is anything to switch — and the directory is a
//    separate, paged, searchable endpoint asked only when the popover opens.
//
// 2. IT SWITCHES BY LOADING THE PAGE, not by navigating. An account boundary is not a
//    route change: query caches, provider state and every module-level value in the bundle
//    were built for the old account. A client-side navigation leaves all of it standing
//    and shows the previous account's rows under the new account's name. A load is the
//    only thing that cannot be subtly wrong, and it is what "switch account" means.
//
// 3. IT LANDS ON /accounts/<id> WITH NO SCREEN NAMED. Which screen is answerable only
//    once the new account's permissions are known, and they are not known here — sending
//    a Limited user to /dashboard greeted them with "Your role does not allow this:
//    reporting.view". AccountIndexRedirect answers it on the other side.
import { useEffect, useId, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";
import { apiFetch } from "@/lib/api-client";
import { useAccount, type AccountSummary } from "@/providers/AccountProvider";
import { useAuth } from "@/providers/AppProviders";
import { cn } from "@/lib/cn";
import {
  ChevronsUpDown,
  Search,
  X,
  Check,
  ArrowRight,
  Settings2,
  LogOut,
} from "lucide-react";

/** One screenful. Bumped rather than paged-and-merged: the list is a person's own
 *  memberships, so it is tens at the very most and a second page is already unusual. */
const PAGE = 20;
/** Typing is not a query. Without this every keystroke was its own cache key and its own
 *  request — on the endpoint this component exists to stop over-fetching. */
const TYPING_SETTLES_MS = 250;

interface Directory {
  rows: AccountSummary[];
  total: number;
  has_more: boolean;
}

/** A workspace row's name can be null — written before the column existed, or an owner
 *  who never had a name on file. Falling back to the role beats rendering a blank line. */
function labelFor(name: string | null, isOwn: boolean, roleLabel: string): string {
  return name?.trim() || (isOwn ? "Personal workspace" : `${roleLabel} account`);
}

export function AccountSwitcher() {
  const { accountId, identity, path } = useAccount();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [settled, setSettled] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const panelId = useId();

  const holdsSeveral = (identity?.account_count ?? 0) > 1;

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(query.trim()), TYPING_SETTLES_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const directory = useQuery({
    queryKey: ["team", "accounts", settled, limit],
    queryFn: () =>
      apiFetch<Directory>(
        `/api/team/accounts?limit=${limit}&q=${encodeURIComponent(settled)}`,
      ),
    // THE WHOLE POINT: no request until somebody opens the control.
    enabled: open && holdsSeveral,
    // Keep the previous page on screen while the next one loads. Without it, "Show more"
    // and every keystroke blanked the list — including the button being clicked, which
    // dropped focus to <body> mid-interaction.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      // Clicking elsewhere is a decision to be elsewhere: close, but do not pull focus
      // back to a control they just chose to leave.
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Escape is "cancel", and cancelling must not strand a keyboard user at the top of
      // the document — the popover unmounts, so focus has to be put back deliberately.
      trigger.current?.focus();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A navigation with the popover still open would leave it floating over the next
  // screen. Reachable without a mousedown: Enter on a focused nav link, or the browser's
  // own back button.
  useEffect(() => setOpen(false), [location.pathname]);

  if (!identity) {
    return (
      <div className="flex h-11 w-full items-center gap-2.5 rounded-[var(--radius-icon)] px-2">
        <div className="size-8 shrink-0 animate-pulse rounded-[var(--radius-ctl)] bg-n-hover" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="h-3 w-24 animate-pulse rounded bg-n-hover" />
          <div className="h-2.5 w-16 animate-pulse rounded bg-n-hover" />
        </div>
      </div>
    );
  }

  function switchTo(next: string) {
    if (next === accountId) {
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    // A LOAD, not a navigation — see (2) above. No screen is named: the new account's
    // permissions decide that, and they are not known on this side of the switch.
    window.location.assign(`/accounts/${next}`);
  }

  const currentLabel = labelFor(
    identity.account_name,
    identity.is_owner,
    identity.role_label,
  );
  const rows = directory.data?.rows ?? [];
  const workspaceInitial = (currentLabel[0] ?? "?").toUpperCase();
  const userInitials = (user?.full_name ?? user?.email ?? identity.user.name ?? identity.user.email ?? "?")
    .trim()
    .slice(0, 1)
    .toUpperCase();

  return (
    <div ref={box} className="relative">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          "group flex h-11 w-full items-center gap-2.5 rounded-[var(--radius-icon)] px-2 text-left transition-all duration-150 ease-out",
          open
            ? "bg-n-surface shadow-xs ring-1 ring-n-line"
            : "hover:bg-brand-hover/60 active:scale-[0.99]",
        )}
      >
        {/* Workspace Avatar / Monogram */}
        <span className="relative flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-ctl)] bg-brand-primary text-[12px] font-semibold text-white shadow-2xs">
          {workspaceInitial}
          <span className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full border border-n-surface bg-emerald-500" />
        </span>

        {/* Text */}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold leading-tight text-n-fg">
            {currentLabel}
          </span>
          <span className="block truncate text-[11px] leading-tight text-n-fg-faint">
            {identity.is_owner ? "Your account" : identity.role_label}
            {user?.full_name ? ` · ${user.full_name}` : ""}
          </span>
        </span>

        {/* Trailing unfold indicator */}
        <ChevronsUpDown
          size={16}
          strokeWidth={1.75}
          className={cn(
            "shrink-0 text-n-fg-faint transition-all duration-200 group-hover:text-n-fg",
            open && "rotate-180 text-brand-primary",
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute bottom-full left-1 z-50 mb-2.5 w-[280px] overflow-hidden rounded-[var(--radius-card)] border-hairline border-n-line bg-n-surface shadow-2xl ring-1 ring-black/5 dark:ring-white/10"
        >
          {/* Section 1: User Profile Header */}
          <div className="border-b border-n-line/60 bg-brand-subtle/40 px-3.5 py-3">
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-primary text-[13px] font-semibold text-white shadow-2xs">
                {userInitials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-n-fg">
                    {user?.full_name ?? identity.user.name ?? "Signed in"}
                  </span>
                  <span className="shrink-0 rounded bg-brand-primary/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-brand-primary">
                    {identity.role_label}
                  </span>
                </div>
                <p className="truncate text-[11.5px] text-n-fg-muted">
                  {user?.email ?? identity.user.email ?? ""}
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Workspaces List */}
          <div className="p-1.5">
            <div className="flex items-center justify-between px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-n-fg-faint">
              <span>Workspaces</span>
              <span className="rounded-full bg-n-hover px-1.5 py-0.5 text-[10px] font-medium text-n-fg-muted">
                {directory.data?.total ?? (holdsSeveral ? identity.account_count : 1)}
              </span>
            </div>

            {holdsSeveral && (
              <div className="relative mb-1.5 px-1">
                <Search
                  size={14}
                  strokeWidth={1.75}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-n-fg-faint"
                  aria-hidden="true"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  autoFocus
                  value={query}
                  aria-label="Filter workspaces"
                  placeholder="Search accounts…"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setLimit(PAGE);
                  }}
                  className="h-8 w-full rounded-[var(--radius-ctl)] border-hairline border-n-line bg-n-canvas pl-8 pr-7 text-[12px] text-n-fg outline-none transition-colors placeholder:text-n-fg-faint focus:border-brand-primary"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      setSettled("");
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-n-fg-faint transition-colors hover:text-n-fg"
                    aria-label="Clear search"
                  >
                    <X size={14} strokeWidth={1.75} />
                  </button>
                )}
              </div>
            )}

            <div className="max-h-56 space-y-0.5 overflow-y-auto px-1">
              {directory.isPending && holdsSeveral && (
                <div className="space-y-1 px-2 py-3">
                  <div className="h-8 w-full animate-pulse rounded bg-n-hover" />
                  <div className="h-8 w-full animate-pulse rounded bg-n-hover" />
                </div>
              )}

              {directory.isError && (
                <p className="px-3 py-3 text-center text-[12px] text-n-critical">
                  Could not load your accounts.
                </p>
              )}

              {directory.isSuccess && rows.length === 0 && (
                <p className="px-3 py-3 text-center text-[12px] text-n-fg-faint">
                  No accounts match “{query}”.
                </p>
              )}

              {/* Single account: show active workspace */}
              {!holdsSeveral && (
                <div className="flex w-full items-center gap-2.5 rounded-[var(--radius-ctl)] bg-brand-subtle px-2.5 py-2 text-left">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-primary text-[11px] font-semibold text-white shadow-2xs">
                    {workspaceInitial}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-n-fg">
                      {currentLabel}
                    </span>
                    <span className="block truncate text-[11px] text-n-fg-faint">
                      {identity.is_owner ? "Personal workspace · Owner" : identity.role_label}
                    </span>
                  </span>
                  <Check size={16} strokeWidth={2} className="shrink-0 text-brand-primary" aria-hidden="true" />
                </div>
              )}

              {/* Multiple accounts: list directory rows */}
              {holdsSeveral &&
                rows.map((account) => {
                  const active = account.account_id === accountId;
                  const rowLabel = labelFor(account.name, account.is_own, account.role_label);
                  const rowInitial = (rowLabel[0] ?? "?").toUpperCase();
                  return (
                    <button
                      key={account.account_id}
                      type="button"
                      aria-current={active ? "true" : undefined}
                      onClick={() => switchTo(account.account_id)}
                      className={cn(
                        "group flex w-full items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-left transition-colors duration-150 ease-out",
                        active
                          ? "bg-brand-subtle font-medium text-n-fg"
                          : "text-n-fg-muted hover:bg-brand-subtle hover:text-n-fg",
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-7 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold transition-colors",
                          active
                            ? "bg-brand-primary text-white shadow-2xs"
                            : "border-hairline border-n-line bg-n-canvas text-n-fg group-hover:border-brand-primary group-hover:text-brand-primary",
                        )}
                      >
                        {rowInitial}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-[12.5px] text-n-fg">
                            {rowLabel}
                          </span>
                          {account.is_own && (
                            <span className="shrink-0 rounded bg-n-hover px-1 py-0.2 text-[9.5px] font-medium text-n-fg-faint">
                              Personal
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-[11px] text-n-fg-faint">
                          {account.is_own ? "Owner" : account.role_label}
                        </span>
                      </span>
                      {active ? (
                        <Check size={16} strokeWidth={2} className="shrink-0 text-brand-primary" aria-hidden="true" />
                      ) : (
                        <ArrowRight
                          size={14}
                          strokeWidth={1.75}
                          className="shrink-0 text-transparent transition-colors group-hover:text-n-fg-faint"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
            </div>

            {holdsSeveral && directory.data?.has_more && (
              <button
                type="button"
                onClick={() => setLimit((n) => n + PAGE)}
                className="mt-1 w-full rounded-[var(--radius-ctl)] py-1.5 text-center text-[11.5px] font-medium text-brand-primary transition-colors hover:bg-brand-subtle"
              >
                Show more ({rows.length} of {directory.data.total})
              </button>
            )}
          </div>

          {/* Section 3: Quick Navigation */}
          <div className="border-t border-n-line/60 p-1">
            <NavLink
              to={path("/settings")}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-[12.5px] font-medium text-n-fg-muted transition-colors hover:bg-brand-subtle hover:text-n-fg"
            >
              <Settings2 size={15} strokeWidth={1.75} className="text-n-fg-faint" aria-hidden="true" />
              <span>Workspace settings</span>
            </NavLink>
          </div>

          {/* Section 4: Log out */}
          <div className="border-t border-n-line/60 p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-[12.5px] font-medium text-n-fg-muted transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            >
              <LogOut size={15} strokeWidth={1.75} aria-hidden="true" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
